import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { ConfigurationState, CountryInfo, EnumeratedTypesRepositoryService, JurisdictionInfo, JurisdictionSelectionComponent, Order, OrderManagerService, OrderRepositoryService, SelectOption, eAssetType, eBusinessSearchCriteriaType, eMatSelectAppearance, eOrderType, eOrderTypeGroup, ePartyType, eQCFormType, OrderTypeCountriesAndJurisdictions } from 'reg-hub-common';
import { EnvironmentUrlService } from '../../services/environment-url/environment-url.service';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { BehaviorSubject, debounceTime, forkJoin, map, Observable, shareReplay, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { MatSelectChange } from '@angular/material/select';
import { EagerFetchRepositoryService } from '../../services/eager-fetch-repository/eager-fetch-repository.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-base-order-information',
  templateUrl: './base-order-information.component.html',
  styleUrls: ['./base-order-information.component.css'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BaseOrderInformationComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  protected onDestroy$ = new Subject<void>();

  order: Order = {
    referenceNumber: "",
    country: "CA",
    jurisdiction: "",
    orderTypeID: eOrderType.notSet
  } as Order;

  isHistorical: boolean = false;
  shouldShowJurisdiction: boolean = true;
  shouldShowJurisdictionMulti: boolean = true;

  orderTypeGroupIDs: eOrderTypeGroup[] = [eOrderTypeGroup.PPSARegistration];
  ppsaRegistrationOrderTypeGroup = eOrderTypeGroup.PPSARegistration;
  @ViewChild(JurisdictionSelectionComponent) jurisdictionSelectionComponent!: JurisdictionSelectionComponent;

  protected baseOrderDetails!: FormGroup;
  protected orderTypes: SelectOption[] = [];
  protected orderTypeCountriesAndJurisdictions: OrderTypeCountriesAndJurisdictions[] = [];
  protected countries: CountryInfo[] = [];
  protected jurisdictions: JurisdictionInfo[] = [];
  protected selectedCountry: string = "CA";
  protected selectedJurisdictions: string[] = [];
  protected orderTypeFieldName: string = "orderTypeID";
  protected appearance: MatFormFieldAppearance = eMatSelectAppearance.outline;
  protected selectedOrderType: string | null = null;
  protected orderIsQC = false;
  protected qcFormTypes: SelectOption[] = [];
  protected isSaving: boolean = false;
  protected isBasic: boolean = false;
  protected _userGroups: SelectOption[] = [];
  protected _bundles: SelectOption[] = [];
  cardContentVersion = 0;
  private readonly SELECT_ALL_CODE = 'selectAll';

  // Kept so a pre-fill from the URL can wait until both lists have loaded
  private orderTypesLoaded$?: Observable<SelectOption[]>;
  private orderTypeJurisdictionsLoaded$?: Observable<OrderTypeCountriesAndJurisdictions[]>;
  // From the URL pre-fill; put on the draft's asset so the next step opens with it filled in
  private prefillSerialNumber: string | null = null;

  jurisdictionFilterControl = new FormControl();
  filteredJurisdictions: BehaviorSubject<JurisdictionInfo[]> = new BehaviorSubject<JurisdictionInfo[]>(this.jurisdictions);
  filteredCountries: BehaviorSubject<CountryInfo[]> = new BehaviorSubject<CountryInfo[]>(this.countries);

  constructor(
    private environmentUrl: EnvironmentUrlService,
    private formBuilder: FormBuilder,
    private ordersRepo: OrderRepositoryService,
    private orderManager: OrderManagerService,
    private enumeratedTypesRepo: EnumeratedTypesRepositoryService,
    private router: Router,
    private route: ActivatedRoute,
    private configurationState: ConfigurationState,
    private eagerFetchService: EagerFetchRepositoryService) { }

  ngOnInit(): void {
    this.configurationState.getUserGroupID().then(userGroupID => {
      this.baseOrderDetails.get('userGroupID')?.setValue(userGroupID);
    });

    this.eagerFetchService.userGroupOptions.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(userGroupOptions => this._userGroups = userGroupOptions);
    this.eagerFetchService.bundleOptions.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(bundleOptions => this._bundles = bundleOptions);

    this.baseOrderDetails = this.formBuilder.group({
      referenceNumber: [this.order.referenceNumber, Validators.required],
      country: [this.order.country, Validators.required],
      jurisdiction: [this.order.jurisdiction, Validators.required],
      orderTypeID: [this.order.orderTypeID, Validators.required],
      userGroupID: [this.order.userGroupID, null],
      bundleID: [this.order.bundleID, null]
    });

    const currentURL = this.router.url;
    if (currentURL.includes('ppsa-registration')) {
      this.orderTypeGroupIDs = [eOrderTypeGroup.PPSARegistration, eOrderTypeGroup.BasicPPSARegistration, eOrderTypeGroup.PPSAChangeManagement];
    } else if (currentURL.includes('ppsa-search')) {
      this.orderTypeGroupIDs = [eOrderTypeGroup.PPSASearch];
      this.formatFormForSearches();
    } else if (currentURL.includes('business')) {
      this.orderTypeGroupIDs = [eOrderTypeGroup.BusinessSearch];
    } else if (currentURL.includes('land')) {
      this.orderTypeGroupIDs = [eOrderTypeGroup.LandServices];
    }

    this.getOrderTypes();
    this.getOrderTypeJurisdictions();
    this.applyQueryParamPrefill();

    if (this.order.orderTypeID === eOrderType.businessSearch) {
      this.baseOrderDetails.addControl('businessSearchCriteria', this.formBuilder.group({
        businessSearchCriteriaTypeID: [eBusinessSearchCriteriaType.Name],
        name: ['']
      }));
    }

    this.jurisdictionFilterControl.valueChanges
      .pipe(
        debounceTime(200),
        startWith(''),
        map(search => this.filterJurisdictions(search)))
      .subscribe(filteredJurisdictions => {
        this.filteredJurisdictions.next(filteredJurisdictions);
      });
  }

  filterJurisdictions(value: string): JurisdictionInfo[] {
    const filterValue = value.toLowerCase();
    return this.jurisdictions.filter(option => option.jurisdictionFullName.toLowerCase().includes(filterValue));
  }

  ngOnDestroy(): void {
    // Signal all subscriptions to complete
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getOrderTypes() {
    this.orderTypesLoaded$ = this.enumeratedTypesRepo.getOrderTypesAsSelectOptions(
      this.environmentUrl.urlAddress,
      this.orderTypeGroupIDs,
      [eOrderType.assetMultiSearch, eOrderType.debtorMultiSearch, eOrderType.amendment, eOrderType.renewal, eOrderType.discharge, eOrderType.reregistration, eOrderType.ultimateBeneficialOwnershipReport])
      .pipe(
        // Only the order types this user can draft
        switchMap(orderTypes => Promise.all(orderTypes.map(orderType =>
          this.configurationState.canDraftOrderType(orderType.value as eOrderType).then(access => access ? orderType : null)))),
        map(orderTypes => orderTypes.filter((orderType): orderType is SelectOption => orderType !== null)),
        shareReplay(1));

    this.orderTypesLoaded$.subscribe(orderTypes => this.orderTypes.push(...orderTypes));
  }

  getQCFormTypes() {
    const lienFormTypes: eQCFormType[] = [eQCFormType.RDd, eQCFormType.RDe, eQCFormType.RHa, eQCFormType.RDf, eQCFormType.RDc, eQCFormType.RDb, eQCFormType.RDa];
    const assignmentFormTypes: eQCFormType[] = [eQCFormType.RG13, eQCFormType.RG14, eQCFormType.RG80];
    var formTypes = this.selectedOrderType?.includes('Assignment') ? assignmentFormTypes : lienFormTypes;

    this.enumeratedTypesRepo.getQcFormTypesAsSelectOptions(this.environmentUrl.urlAddress, formTypes)
      .subscribe(qcFormTypes => {
        this.qcFormTypes = qcFormTypes.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      });
  }

  protected handleCountryChangeEvent() {
    this.orderIsQC = false;

    this.handleOrderTypeAvailableJurisdictions();

    this.updateQCFormTypeControl();
  }

  protected handleJurisdictionChangeEvent() {
    const jurisdiction = this.baseOrderDetails.get('jurisdiction')!.value;
    this.orderIsQC = jurisdiction == "QC";

    this.updateQCFormTypeControl();
  }

  protected getOrderTypeJurisdictions() {
    this.orderTypeJurisdictionsLoaded$ = this.enumeratedTypesRepo.getOrderTypeCountriesAndJurisdictions().pipe(shareReplay(1));
    this.orderTypeJurisdictionsLoaded$.subscribe(orderTypes => this.orderTypeCountriesAndJurisdictions = orderTypes);
  }

  // Lets other pages start an order with the type and jurisdiction(s) already chosen, e.g. an alert's
  // "Asset Search" action: /orders/ppsa-search?orderType=AssetSearch&jurisdictions=ON&jurisdictions=BC&serialNumber=….
  // Anything this page doesn't offer, or the user can't draft, is ignored, leaving the form as normal.
  private applyQueryParamPrefill() {
    const params = this.route.snapshot.queryParamMap;
    const orderType = params.get('orderType');
    if (!orderType || !this.orderTypesLoaded$ || !this.orderTypeJurisdictionsLoaded$) {
      return;
    }

    forkJoin([this.orderTypesLoaded$, this.orderTypeJurisdictionsLoaded$]).pipe(takeUntil(this.onDestroy$)).subscribe(([orderTypes]) => {
      if (!orderTypes.some(t => t.value === orderType)) {
        return;
      }

      // Same path as picking the order type by hand; it sets up the jurisdiction list (and clears any jurisdiction)
      this.baseOrderDetails.get('orderTypeID')!.setValue(orderType);
      this.onOrderTypeSelectionChange();
      this.prefillSerialNumber = params.get('serialNumber')?.trim() || null;

      const available = new Set(this.jurisdictions.map(j => j.jurisdictionCode).filter(code => code !== this.SELECT_ALL_CODE));
      const jurisdictions = params.getAll('jurisdictions').filter(code => available.has(code));
      if (jurisdictions.length === 0) {
        return;
      }

      const multiControl = this.baseOrderDetails.get(['searchParameters', 'searchJurisdictions']);
      if (this.shouldShowJurisdictionMulti && multiControl) {
        multiControl.setValue(jurisdictions);
        this.selectedJurisdictions = jurisdictions;
      } else if (this.shouldShowJurisdiction && jurisdictions.length === 1) {
        this.baseOrderDetails.get('jurisdiction')!.setValue(jurisdictions[0]);
        this.handleJurisdictionChangeEvent();
      }
    });
  }

  private updateJurisdictionsForCountry(
    countryCode: string | null | undefined,
    config: OrderTypeCountriesAndJurisdictions,
    jurisdictionControl: AbstractControl | null
  ) {
    // Find the selected country (fallback to first if not found)
    const selectedCountry =
      config.countries.find(c => c.countryCode === countryCode) ??
      config.countries[0];

    let jurisdictions: JurisdictionInfo[] = selectedCountry
      ? [...selectedCountry.jurisdictions]
      : [];

    if (this.shouldShowJurisdictionMulti) {
      jurisdictions.unshift({
        jurisdictionCode: 'selectAll',
        jurisdictionFullName: this.transloco.translate('orders.selectAll')
      });
    }

    this.jurisdictions = jurisdictions;
    this.filteredJurisdictions.next(jurisdictions);

    if (jurisdictions.length === 1) {
      jurisdictionControl?.setValue(jurisdictions[0].jurisdictionCode);
      jurisdictionControl?.disable();
    } else {
      jurisdictionControl?.enable();
    }
  }

  protected onOrderTypeSelectionChange() {
    this.selectedOrderType = this.baseOrderDetails.get('orderTypeID')!.value;
    this.isBasic = (this.selectedOrderType == "BasicDischarge" || this.selectedOrderType == "BasicRenewal");
    const isPpsaSearch = this.selectedOrderType === eOrderType.assetSearch || 
      this.selectedOrderType === eOrderType.debtorSearch ||
      this.selectedOrderType === eOrderType.registrationNumberSearch || 
      this.selectedOrderType === eOrderType.certifiedDebtorSearch;

    if (this.isBasic) {
      this.formatFormForBasics();
    } else if (isPpsaSearch) {
      this.formatFormForSearches();
      this.formatFormForRegistrations(); // Don't ask why (it's because registration number searches require a lien object)
    } else {
      this.formatFormForRegistrations();
    }

    if (this.shouldDisableHistoricalCheckbox()) {
      this.isHistorical = false;
    }

    const jurisdictionControl = this.baseOrderDetails.get('jurisdiction');
    this.handleOrderTypeAvailableJurisdictions();

    this.orderIsQC = jurisdictionControl?.value === 'QC';
    this.updateQCFormTypeControl();
  }

  private handleOrderTypeAvailableJurisdictions() {
    const jurisdictionControl = this.baseOrderDetails.get('jurisdiction');
    const countryControl = this.baseOrderDetails.get('country');

    this.shouldShowJurisdiction = ((this.selectedOrderType !== eOrderType.businessSearch && this.selectedOrderType !== eOrderType.pepsAndSanctions) 
      || countryControl?.value === "US") && 
    this.selectedOrderType !== eOrderType.assetSearch && this.selectedOrderType !== eOrderType.debtorSearch;
    this.shouldShowJurisdictionMulti = this.selectedOrderType === eOrderType.assetSearch || this.selectedOrderType === eOrderType.debtorSearch;

    jurisdictionControl?.reset();

    this.orderTypeCountriesAndJurisdictions.forEach(orderTypeCountriesAndJurisdiction => {
      if (orderTypeCountriesAndJurisdiction.orderTypeID == this.selectedOrderType) {
        let countries = [...orderTypeCountriesAndJurisdiction.countries];

        if (this.selectedOrderType === eOrderType.businessReport) {
          countries = countries.filter(c => c.countryCode !== "US");
        }
        this.filteredCountries.next(countries);
        this.countries = countries;

        // If there is only one country, auto-select it
        if (countries.length === 1) {
          this.selectedCountry = countries[0].countryCode;
          countryControl?.setValue(this.selectedCountry, { emitEvent: false });
          countryControl?.disable();
        } else {
          countryControl?.enable();
        }

        // Pull jurisdictions based on selected country
        const effectiveCountryCode =
          countryControl?.value ?? countries[0]?.countryCode;

        this.updateJurisdictionsForCountry(
          effectiveCountryCode,
          orderTypeCountriesAndJurisdiction,
          jurisdictionControl);
      }
    });

    //If we're not showing jurisdiction or we are using multiple jurisdictions, set to multi
    if (!this.shouldShowJurisdiction || this.shouldShowJurisdictionMulti) {
      this.baseOrderDetails.get('jurisdiction')?.setValue("_M");
    }
  }

  private updateQCFormTypeControl() {
    const qcFormTypeIDControl = this.baseOrderDetails.get('lien')?.get('qcFormTypeID');

    if (!qcFormTypeIDControl) {
      return;
    }

    if (this.shouldShowQCFormTypes()) {
      qcFormTypeIDControl.addValidators(Validators.required);
      this.getQCFormTypes();
    } else {
      qcFormTypeIDControl.clearValidators();
      qcFormTypeIDControl.reset();
    }
  }

  onJurisdictionsSelectionChange(event: MatSelectChange) {
    this.selectedJurisdictions = event.value || [];
    const control = this.baseOrderDetails.get(['searchParameters', 'searchJurisdictions'])!;
    const allCodes = this.jurisdictions.filter(c => c.jurisdictionCode !== this.SELECT_ALL_CODE).map(j => j.jurisdictionCode);

    if (this.selectedJurisdictions.includes(this.SELECT_ALL_CODE)) {
      if (this.selectedJurisdictions.length === 1) {
        control.setValue(allCodes);
      } else {
        control.setValue([]);
      }

      return;
    }
  }

  saveContents(): boolean {
    this.isSaving = true;
    const orderTypeIDValue = this.baseOrderDetails.get('orderTypeID')!.value;

    // Add extra required groups depending on request type
    if (this.orderTypeGroupIDs[0] == eOrderTypeGroup.BusinessSearch) {
      const orderTypeIDValue = this.baseOrderDetails.get('orderTypeID')!.value;
      if (orderTypeIDValue == "BusinessSearch") {
        this.baseOrderDetails.addControl('businessSearchCriteria', this.formBuilder.group({
          businessSearchCriteriaTypeID: [eBusinessSearchCriteriaType.Name],
          name: [""]
        }));
      }
      if (orderTypeIDValue == "BusinessReport") {
        this.baseOrderDetails.addControl('businessReportCriteria', this.formBuilder.group({
          number: [""]
        }));
      }
    }

    if (!this.baseOrderDetails.valid) {
      this.baseOrderDetails.markAllAsTouched();
      this.baseOrderDetails.updateValueAndValidity();

      return false;
    }

    const orderToCreate = this.baseOrderDetails.getRawValue();

    if (orderTypeIDValue === eOrderType.debtorSearch || orderTypeIDValue === eOrderType.certifiedDebtorSearch) {
      orderToCreate['parties'] = [{ 'partyTypeID': ePartyType.individualDebtor }];
    } else if (orderTypeIDValue === eOrderType.assetSearch) {
      orderToCreate['assets'] = [{ 'assetTypeID': eAssetType.MotorVehicle, 'serialNumber': this.prefillSerialNumber }];
    } else if (orderTypeIDValue === eOrderType.landTitleSearch) {
      orderToCreate['landTitleSearchCriteria'] = {};
    }

    let jurisdictionSelections = this.baseOrderDetails.get('searchParameters')?.get('searchJurisdictions')?.value;

    if (jurisdictionSelections) {
      jurisdictionSelections = jurisdictionSelections.filter((c: string) => c !== this.SELECT_ALL_CODE);
    }

    if (orderTypeIDValue === eOrderType.assetSearch || orderTypeIDValue === eOrderType.debtorSearch) {
      if (jurisdictionSelections &&
        jurisdictionSelections.length > 1) {

        if (orderTypeIDValue === eOrderType.assetSearch) {
          orderToCreate['orderTypeID'] = eOrderType.assetMultiSearch;
        } else if(orderTypeIDValue === eOrderType.debtorSearch) {
          orderToCreate['orderTypeID'] = eOrderType.debtorMultiSearch;
        }

        delete (orderToCreate['lien']);
        delete (orderToCreate['country']);
        delete (orderToCreate['jurisdiction']);
        orderToCreate['searchParameters']['searchJurisdictions'] = jurisdictionSelections.map((j: string) => {
          return { jurisdiction: j };
        });
      } else {
        orderToCreate['jurisdiction'] = jurisdictionSelections.at(0);
        delete orderToCreate.searchParameters.searchJurisdictions;
      }
    } else if (orderToCreate.searchParameters?.searchJurisdictions) {
      delete orderToCreate.searchParameters.searchJurisdictions;
    }

    this.ordersRepo.createNewOrder(orderToCreate, this.isHistorical)
      .subscribe(result => {
        this.isSaving = false;
        const order = (result as any).resource;
        this.order = order;
        this.orderManager.updateOrder(order);
        if (this.order.id) {
          var redirectUrl: string[] = [];

          if (this.orderTypeGroupIDs[0] == eOrderTypeGroup.PPSARegistration) {
            redirectUrl = ['orders', 'ppsa-registration', order.id];
          } else if (this.orderTypeGroupIDs[0] == eOrderTypeGroup.PPSASearch) {
            redirectUrl = ['orders', 'ppsa-search', order.id];
          } else if (this.orderTypeGroupIDs[0] == eOrderTypeGroup.BusinessSearch) {
            redirectUrl = ['orders', 'business', order.id];
          } else if (this.orderTypeGroupIDs[0] === eOrderTypeGroup.LandServices) {
            redirectUrl = ['orders', 'land', order.id];
          }

          this.router.navigate(redirectUrl);
        }
      });

    // do validation here
    return true;
  }

  // TODO: Fill out validation
  validateContents(form?: NgForm): StepValidationState {
    return StepValidationState.ValidationSuccess;
  }

  private formatFormForRegistrations() {
    this.baseOrderDetails.removeControl('referenceRegistrationInformation');
    this.baseOrderDetails.addControl('lien', this.formBuilder.group({
      qcFormTypeID: [null]
    }))
  }

  private formatFormForBasics() {
    this.isHistorical = false;
    this.baseOrderDetails.removeControl('lien');
    this.baseOrderDetails.addControl('referenceRegistrationInformation', this.formBuilder.group({
      originalQcFormTypeID: [null]
    }));
  }

  private formatFormForSearches() {
    let searchParametersControl = this.baseOrderDetails.get('searchParameters');

    if (!searchParametersControl) {
      searchParametersControl = this.formBuilder.group({});
      this.baseOrderDetails.addControl('searchParameters', searchParametersControl);
    }

    const searchParametersGroup = searchParametersControl as FormGroup;
    if (!this.selectedOrderType || this.selectedOrderType === eOrderType.assetSearch || this.selectedOrderType === eOrderType.debtorSearch) {
      searchParametersGroup.addControl('searchJurisdictions', this.formBuilder.control([], Validators.required), { emitEvent: false });
    } else if (this.selectedOrderType === eOrderType.registrationNumberSearch || this.selectedOrderType === eOrderType.certifiedDebtorSearch) {
      searchParametersGroup.removeControl('searchJurisdictions', { emitEvent: false });
    }
  }

  protected shouldShowQCFormTypes(): boolean {
    return this.orderIsQC && (this.selectedOrderType == 'Lien' || (this.selectedOrderType?.includes('Assignment') ?? false))
  }

  protected shouldDisableHistoricalCheckbox() {
    return this.isBasic || this.selectedOrderType === "Assignment";
  }
}
